/**
 * The one chart.
 *
 * Every chart in this application is this component with different series:
 * a price history, a comparison, an indicator over time, a backtest's
 * equity curve. Creating the chart, theming it, tearing it down, the empty
 * and loading states, the legend and the way out to TradingView are
 * decided here once, so a chart learned on one screen is already
 * understood on the next.
 *
 * Callers describe what to draw rather than how. A second, nearly
 * identical chart is a bug in this codebase rather than a shortcut -- it
 * is how two screens start disagreeing about what a falling price looks
 * like, and about which series is which colour.
 */

import {
  AreaSeries,
  CandlestickSeries,
  ColorType,
  CrosshairMode,
  HistogramSeries,
  type IChartApi,
  type ISeriesApi,
  LineSeries,
  type MouseEventParams,
  type PriceFormatCustom,
  type SeriesType,
  createChart,
} from "lightweight-charts";
import { useCallback, useEffect, useRef, useState } from "react";

import { RotateCcw } from "lucide-react";

import { TradingViewLink } from "@/components/TradingViewLink";
import { AVERAGE_WIDTH, FALL, NEUTRAL, PRICE_WIDTH, RISE, THRESHOLD } from "@/lib/chartPalette";
import { tokenColour, translucent } from "@/lib/tokenColour";
import { formatCount, formatDay, formatPrice, formatWhole } from "@/lib/format";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";

/** How opaque volume bars are drawn: faint, under the price. */
const BAR_OPACITY = 0.35;

/** One session of a candle series. */
export interface Candle {
  time: string;
  open: number;
  high: number;
  low: number;
  close: number;
}

/** One point of a line or a bar. */
export interface Point {
  time: string;
  value: number;
  /** A per-bar colour, for a histogram whose bars mean two things. */
  color?: string;
  /**
   * The figures behind this one, written under it in the crosshair
   * reading. A derived value -- a ratio, a share, a spread -- loses what
   * it was computed from, and the raw counts are usually the thing a
   * reader wants back at the moment they ask about one session.
   */
  detail?: string;
}

/** A horizontal rule on a series, such as an oscillator's thresholds. */
export interface Threshold {
  value: number;
  label?: string;
}

/** What every series carries, whatever its shape. */
interface Common {
  label: string;
  /**
   * Which pane to draw in. Nought is the main one; anything else gets a
   * band of its own underneath, which is what an oscillator on a nought
   * to a hundred scale needs -- drawn over a price it would be a flat
   * line along the bottom.
   */
  pane?: number;
  /** Rules to draw across the series, such as 30 and 70 on an RSI. */
  thresholds?: Threshold[];
  /**
   * What this series' figures are, when they are not the chart's own.
   * A chart with panes can hold two units at once -- a percentage over an
   * oscillator -- and the unit belongs to the series rather than to the
   * frame around it.
   */
  scale?: Scale;
}

/** Something to draw, and what to call it. */
export type Series =
  | (Common & { kind: "candles"; points: Candle[] })
  | (Common & {
      kind: "line";
      colour: string;
      points: Point[];
      width?: number;
      /** Drawn in dashes: a line that is not a record, such as a forecast. */
      dashed?: boolean;
    })
  | (Common & { kind: "area"; colour: string; points: Point[] })
  | (Common & { kind: "bars"; points: Point[] });

/**
 * What a chart's figures are, which decides how they are written: a price,
 * a change in per cent (signed), a count, a share in per cent (a level,
 * unsigned: 45.2% of contracts long did not rise by 45.2%), or a sum in
 * rupees crore (whole and grouped: the market's revenue on the price scale
 * read "15000000.00").
 */
export type Scale = "price" | "percent" | "count" | "share" | "crore";

/** What the crosshair is over, what each series was worth, and where it is. */
interface Hovered {
  day: string;
  figures: { label: string; colour: string; value: number; scale: Scale; detail?: string }[];
  /** Where in the plot the crosshair sits, and how big the plot is. */
  at: { x: number; y: number; width: number; height: number };
}

/** An instrument a chart draws, and how to leave for it. */
export interface ChartInstrument {
  label: string;
  /** What TradingView calls it, when this platform knows. */
  symbol?: string | undefined;
  /** Whether that symbol was guessed rather than confirmed. */
  derived?: boolean | undefined;
}

interface ChartProps {
  series: Series[];
  /** The instruments drawn, offered as links out. */
  instruments?: ChartInstrument[];
  /** Extra readings beside the legend, such as each line's total return. */
  readings?: { label: string; value: string; tone?: "gain" | "loss" }[];
  loading?: boolean;
  /** What to say when there is nothing to draw. */
  empty?: string;
  /**
   * What the figures are, which decides how the axis and the crosshair
   * reading are written. A count is written whole: two decimal places on
   * a number of companies is noise pretending to be precision.
   */
  scale?: Scale;
  /** How tall each pane past the first is drawn. */
  paneHeight?: number;
  height?: number;
  className?: string;
}

const DEFAULT_HEIGHT = 360;

/**
 * The most bars of empty space to leave at each end.
 *
 * Enough to be clearly a margin rather than a rounding error on a long
 * history. A short series gets a tenth of its length, and never less than
 * half a bar: six bars either side of four yearly points left the points
 * in the middle quarter of the chart.
 */
const EDGE_BARS = 6;

/** How tall a pane past the first is drawn. */
const DEFAULT_PANE_HEIGHT = 110;

/** The scale volume and other bar series are pinned to. */
const BAR_SCALE = "bars";

/**
 * Draw the series.
 *
 * @param props - What to draw, what to call it, and how tall.
 * @returns The chart, its legend, and the way out to TradingView.
 */
export function Chart({
  series,
  instruments = [],
  readings = [],
  loading = false,
  empty = "Nothing to draw",
  scale = "price",
  paneHeight = DEFAULT_PANE_HEIGHT,
  height = DEFAULT_HEIGHT,
  className,
}: ChartProps): React.JSX.Element {
  const holder = useRef<HTMLDivElement>(null);
  const chart = useRef<IChartApi | null>(null);
  // The frame the chart opens in, so "reset" has something exact to
  // return to rather than re-fitting whatever happens to be drawn.
  const opening = useRef<{ from: number; to: number } | null>(null);
  const [moved, setMoved] = useState(false);
  // What the crosshair is over: the session, and each series' figure on
  // it. A chart of four lines is unreadable without this -- the axis says
  // what one of them was worth and leaves the rest to be guessed at.
  const [hovered, setHovered] = useState<Hovered | null>(null);
  const { appearance } = useTheme();
  const drawable = series.filter((one) => one.points.length > 0);
  const hasBars = drawable.some((one) => one.kind === "bars");
  // The main plot keeps its height and each pane under it adds its own
  // band, rather than every pane carving room out of one fixed height:
  // two bands in 360 pixels left the middle pane about thirty tall.
  const lowerPanes = new Set(drawable.flatMap((one) => ((one.pane ?? 0) > 0 ? [one.pane] : [])))
    .size;
  const total = height + lowerPanes * paneHeight;

  useEffect(() => {
    const element = holder.current;
    if (element === null || drawable.length === 0) {
      return undefined;
    }

    // Read from the stylesheet as the chart is built, which is every time
    // the theme changes (`appearance` below): a canvas cannot follow a
    // token by itself.
    const text = tokenColour("var(--chart-text)");
    const grid = tokenColour("var(--chart-grid)");
    const crosshair = tokenColour("var(--chart-crosshair)");
    const created = createChart(element, {
      height: total,
      layout: {
        background: { type: ColorType.Solid, color: "transparent" },
        textColor: text,
        attributionLogo: false,
      },
      grid: {
        vertLines: { color: grid },
        horzLines: { color: grid },
      },
      rightPriceScale: {
        borderVisible: false,
        // Room at the bottom only when something is pinned there.
        scaleMargins: { top: 0.08, bottom: hasBars ? 0.26 : 0.08 },
      },
      timeScale: {
        borderVisible: false,
        // Both edges deliberately unpinned. Pinned, the chart stops dead
        // at the first and last session, which makes the newest bar sit
        // flush against the price scale with nowhere to put a crosshair
        // reading, and gives a reader no room to drag the series clear of
        // either end to look at it.
        fixLeftEdge: false,
        fixRightEdge: false,
        // Down from the library's half a pixel, so a long series opens
        // whole: twenty years of closes (a backtest from 2005, a fund's
        // Max) is over five thousand points, which at half a pixel apiece
        // would not fit, and the chart opened on only its last few years.
        minBarSpacing: 0.05,
      },
      // The library's own crosshair labels are a dark navy in both themes.
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: crosshair, labelBackgroundColor: crosshair },
        horzLine: { color: crosshair, labelBackgroundColor: crosshair },
      },
      autoSize: true,
    });
    chart.current = created;

    // Kept by series so the crosshair can name what it is over. The
    // library hands back the series it drew, not the label we gave it.
    const named = new Map<ISeriesApi<SeriesType>, Series>();
    for (const one of drawable) {
      named.set(draw(created, one, scale), one);
    }

    // Panes past the first are given a fixed band rather than an equal
    // share: an oscillator is read for its shape against its thresholds,
    // and half the chart is far more room than that needs. Set as stretch
    // factors in proportion to the height they were added to, which the
    // library honours for every pane at once; `setHeight` on the second of
    // two bands took its room back from the first.
    created.panes().forEach((pane, index) => {
      pane.setStretchFactor(index === 0 ? height : paneHeight);
    });

    if (hasBars) {
      created.priceScale(BAR_SCALE).applyOptions({
        // Its own scale, pinned to the bottom quarter: on one scale with
        // the price, volume is either invisible or it flattens the price
        // into a line.
        scaleMargins: { top: 0.8, bottom: 0 },
        borderVisible: false,
      });
    }
    // Framed with a margin at both ends rather than flush to the data, so
    // there is somewhere to drag to and the first and last sessions are
    // not pressed against the edges of the frame.
    const longest = Math.max(...drawable.map((one) => one.points.length));
    const edge = Math.max(0.5, Math.min(EDGE_BARS, longest / 10));
    const frame = { from: -edge, to: longest - 1 + edge };
    opening.current = frame;
    created.timeScale().setVisibleLogicalRange(frame);
    setMoved(false);

    // Watching the range rather than the pointer: a reader moves the
    // chart by dragging, by the wheel, by pinching and by the keyboard,
    // and all of them end here.
    const watch = (range: { from: number; to: number } | null): void => {
      setMoved(range !== null && !sameFrame(range, frame));
    };
    created.timeScale().subscribeVisibleLogicalRangeChange(watch);

    // What each series wrote under its points, by session. Built once
    // rather than searched on every pointer move: a full history is
    // thousands of points, and a hover would walk all of them.
    const detailed = new Map<string, Map<string, string>>();
    for (const one of drawable) {
      if (one.kind === "candles") {
        continue;
      }
      const byDay = new Map<string, string>();
      for (const point of one.points) {
        if (point.detail !== undefined) {
          byDay.set(point.time, point.detail);
        }
      }
      if (byDay.size > 0) {
        detailed.set(one.label, byDay);
      }
    }

    const follow = (event: MouseEventParams): void => {
      // Off the plot entirely: cleared rather than frozen on the last
      // session it saw, which would read as a reading of right now.
      //
      // The time is narrowed rather than stringified: the library's own
      // type also admits a business-day object, and every series here is
      // handed ISO dates.
      if (typeof event.time !== "string" || event.point === undefined) {
        setHovered(null);
        return;
      }
      const day = event.time;
      const { x, y } = event.point;
      setHovered({
        day,
        at: { x, y, width: element.clientWidth, height: element.clientHeight },
        figures: [...event.seriesData]
          .map(([series, point]) => ({
            series: named.get(series),
            value: figureOf(point),
          }))
          .flatMap(({ series, value }) => {
            if (series === undefined || value === null || series.kind === "bars") {
              return [];
            }
            const detail = detailed.get(series.label)?.get(day);
            return [
              {
                label: series.label,
                colour: colourOf(series),
                value,
                scale: series.scale ?? scale,
                ...(detail === undefined ? {} : { detail }),
              },
            ];
          }),
      });
    };
    created.subscribeCrosshairMove(follow);

    return () => {
      created.unsubscribeCrosshairMove(follow);
      created.timeScale().unsubscribeVisibleLogicalRangeChange(watch);
      created.remove();
      chart.current = null;
      opening.current = null;
      setHovered(null);
    };
    // `drawable` is rebuilt on every render; `series` is what a caller
    // actually changes, and is what this should redraw for.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series, total, appearance, scale, hasBars, paneHeight]);

  const reset = useCallback(() => {
    const frame = opening.current;
    if (chart.current !== null && frame !== null) {
      chart.current.timeScale().setVisibleLogicalRange(frame);
    }
  }, []);

  if (drawable.length === 0) {
    return (
      <div
        className={cn("rounded-lg border", loading && "animate-pulse bg-muted/40", className)}
        style={{ height }}
        role="img"
        aria-label={loading ? "Chart loading" : empty}
      >
        {!loading && (
          <p className="flex h-full items-center justify-center text-sm text-muted-foreground">
            {empty}
          </p>
        )}
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2">
        <ul className="flex flex-wrap gap-x-5 gap-y-1 text-xs" aria-label="Series drawn">
          {drawable
            .filter((one) => one.kind !== "bars")
            .map((one) => (
              <li key={one.label} className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className="h-0.5 w-3 rounded"
                  style={
                    one.kind === "line" || one.kind === "area"
                      ? { backgroundColor: one.colour }
                      : // Candles are both colours at once.
                        { backgroundImage: `linear-gradient(90deg, ${RISE} 50%, ${FALL} 50%)` }
                  }
                />
                <span className="text-muted-foreground">{one.label}</span>
                {readings
                  .filter((reading) => reading.label === one.label)
                  .map((reading) => (
                    <span
                      key={reading.label}
                      className={cn(
                        "tabular font-medium",
                        reading.tone === "gain" && "text-gain",
                        reading.tone === "loss" && "text-loss",
                      )}
                    >
                      {reading.value}
                    </span>
                  ))}
              </li>
            ))}
        </ul>
        <div className="flex flex-wrap items-center gap-3">
          {moved && (
            // Offered only once there is something to undo: a reset that
            // is always on screen is a button that does nothing nearly
            // every time it is looked at.
            <button
              type="button"
              onClick={reset}
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
            >
              <RotateCcw className="h-3 w-3" />
              Reset view
            </button>
          )}
          {instruments.map((instrument) => (
            <TradingViewLink
              key={instrument.label}
              label={instrument.label}
              symbol={instrument.symbol}
              derived={instrument.derived}
            />
          ))}
        </div>
      </div>
      <div className="relative">
        {hovered !== null && hovered.figures.length > 0 && (
          // Over the plot rather than beside it: a reading that appears
          // in the margin makes the eye leave the line it is following.
          <div
            role="group"
            aria-label="Crosshair reading"
            className="pointer-events-none absolute z-10 whitespace-nowrap rounded-md border bg-popover/95 px-2.5 py-1.5 text-xs shadow-sm"
            style={placed(hovered.at)}
          >
            <div className="mb-0.5 text-muted-foreground">{formatDay(hovered.day)}</div>
            {hovered.figures.map((figure) => (
              <div key={figure.label}>
                <div className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="h-0.5 w-3 shrink-0 rounded"
                    style={{ backgroundColor: figure.colour }}
                  />
                  <span className="text-muted-foreground">{figure.label}</span>
                  <span className="tabular ml-auto font-medium">
                    {written(figure.value, figure.scale)}
                  </span>
                </div>
                {figure.detail !== undefined && (
                  <div className="tabular pl-5 text-muted-foreground">{figure.detail}</div>
                )}
              </div>
            ))}
          </div>
        )}
        <div
          ref={holder}
          style={{ height: total }}
          data-testid="chart"
          className="motion-safe:animate-fade-in"
          // The conventional gesture, and the one somebody tries first. The
          // button above says it is there; this is how it is reached
          // without looking away from the chart.
          onDoubleClick={reset}
        />
      </div>
    </div>
  );
}

/**
 * The library's dashed line style, by value. Its `LineStyle` enum is a
 * runtime object, and naming the number keeps the chart stub from having to
 * carry one.
 */
const DASHED = 2;

/**
 * Add one series to a chart.
 *
 * @param chart - The chart to add it to.
 * @param series - What to draw.
 * @param scale - What the chart's figures are, unless the series says.
 */
function draw(chart: IChartApi, series: Series, scale: Scale): ISeriesApi<SeriesType> {
  const pane = series.pane ?? 0;
  const drawn = add(chart, series, series.scale ?? scale, pane);
  for (const threshold of series.thresholds ?? []) {
    drawn.createPriceLine({
      price: threshold.value,
      color: tokenColour(THRESHOLD),
      lineWidth: AVERAGE_WIDTH,
      lineStyle: DASHED,
      axisLabelVisible: true,
      title: threshold.label ?? "",
    });
  }
  return drawn;
}

/**
 * Read one point's figure, whatever shape of series it came from.
 *
 * @param point - The point the crosshair is over.
 * @returns Its figure -- a candle's close, anything else's value -- or
 *   null on a session the series has no point for.
 */
function figureOf(point: unknown): number | null {
  const found = point as { close?: number; value?: number };
  return found.close ?? found.value ?? null;
}

/**
 * The colour a series is drawn in.
 *
 * @param series - The series.
 * @returns Its colour, or the neutral one for shapes that carry none.
 */
function colourOf(series: Series): string {
  return series.kind === "line" || series.kind === "area" ? series.colour : NEUTRAL;
}

/**
 * Add one series and hand back what was added.
 *
 * @param chart - The chart to add it to.
 * @param series - What to draw.
 * @param scale - What this series' figures are.
 * @param pane - Which pane to draw in.
 * @returns The series, so rules can be drawn across it.
 */
function add(
  chart: IChartApi,
  series: Series,
  scale: Scale,
  pane: number,
): ReturnType<IChartApi["addSeries"]> {
  if (series.kind === "candles") {
    const up = tokenColour(RISE);
    const down = tokenColour(FALL);
    const candles = chart.addSeries(
      CandlestickSeries,
      { upColor: up, downColor: down, borderVisible: false, wickUpColor: up, wickDownColor: down },
      pane,
    );
    candles.setData(series.points);
    return candles;
  }

  if (series.kind === "bars") {
    const bars = chart.addSeries(
      HistogramSeries,
      { priceFormat: { type: "volume" }, priceScaleId: BAR_SCALE },
      pane,
    );
    // Each bar's own colour, faint: volume is context for the price, not a
    // figure to compete with it. Resolved once per colour, not per bar.
    const shades = new Map<string, string>();
    const shade = (colour: string): string => {
      const known = shades.get(colour);
      if (known !== undefined) {
        return known;
      }
      const made = translucent(tokenColour(colour), BAR_OPACITY);
      shades.set(colour, made);
      return made;
    };
    bars.setData(
      series.points.map((point) =>
        point.color === undefined ? point : { ...point, color: shade(point.color) },
      ),
    );
    return bars;
  }

  if (series.kind === "area") {
    const colour = tokenColour(series.colour);
    const area = chart.addSeries(
      AreaSeries,
      {
        lineColor: colour,
        topColor: translucent(colour, 0.33),
        bottomColor: translucent(colour, 0.02),
        lineWidth: PRICE_WIDTH,
        ...priceFormat(scale),
      },
      pane,
    );
    area.setData(series.points);
    return area;
  }

  const line = chart.addSeries(
    LineSeries,
    {
      color: tokenColour(series.colour),
      lineWidth: series.width === PRICE_WIDTH ? PRICE_WIDTH : AVERAGE_WIDTH,
      ...(series.dashed === true ? { lineStyle: DASHED } : {}),
      lastValueVisible: false,
      priceLineVisible: false,
      ...priceFormat(scale),
    },
    pane,
  );
  line.setData(series.points);
  return line;
}

/**
 * Whether two frames are the same, allowing for the chart's own rounding.
 *
 * The library reports the range it settled on rather than the one it was
 * given, and those differ by a fraction of a bar. Compared exactly, a
 * chart would announce itself as moved the moment it was drawn.
 *
 * @param one - A frame.
 * @param other - The frame to compare it against.
 * @returns Whether they are the same frame.
 */
function sameFrame(
  one: { from: number; to: number },
  other: { from: number; to: number },
): boolean {
  return Math.abs(one.from - other.from) < 0.5 && Math.abs(one.to - other.to) < 0.5;
}

/** How far the reading sits from the cursor. */
const READING_GAP = 14;

/**
 * Place the reading beside the cursor, on whichever side has room.
 *
 * Anchored to the far edge rather than the near one when the cursor is
 * past halfway, which flips it without anybody having to know how wide it
 * is -- and its width depends on the longest instrument name in it, so
 * nobody does until it has been drawn.
 *
 * @param at - Where the crosshair is, and how big the plot is.
 * @returns Where to put the reading.
 */
function placed(at: Hovered["at"]): React.CSSProperties {
  const past = { right: at.x > at.width / 2, bottom: at.y > at.height / 2 };
  return {
    left: past.right ? undefined : at.x + READING_GAP,
    right: past.right ? at.width - at.x + READING_GAP : undefined,
    top: past.bottom ? undefined : at.y + READING_GAP,
    bottom: past.bottom ? at.height - at.y + READING_GAP : undefined,
  };
}

/**
 * How the axis should format a scale.
 *
 * @param scale - What the figures are.
 * @returns The options to pass the library, which are none for a price --
 *   its own default. A count's axis is written whole, as its reading is:
 *   left to the default it read "40.00" people.
 */
function priceFormat(scale: Scale): {
  priceFormat?:
    { type: "percent" | "price"; precision?: number; minMove?: number } | PriceFormatCustom;
} {
  if (scale === "percent" || scale === "share") {
    return { priceFormat: { type: "percent" } };
  }
  if (scale === "crore") {
    return { priceFormat: { type: "custom", formatter: formatWhole, minMove: 1 } };
  }
  return scale === "count" ? { priceFormat: { type: "price", precision: 0, minMove: 1 } } : {};
}

/**
 * Write one figure the way its scale is read.
 *
 * @param value - The figure.
 * @param scale - What it is.
 * @returns The figure, written.
 */
function written(value: number, scale: Scale): string {
  if (scale === "percent") {
    return `${value > 0 ? "+" : ""}${value.toFixed(2)}%`;
  }
  if (scale === "share") {
    return `${value.toFixed(1)}%`;
  }
  if (scale === "crore") {
    return formatWhole(value);
  }
  // A count is whole: two decimal places on a number of companies is
  // noise pretending to be precision.
  return scale === "count" ? formatCount(Math.round(value)) : formatPrice(String(value));
}
